/**
 * The export-policy acknowledgment screen — §8 item 3.
 *
 * Phase 3's middleware returns `403 EXPORT_POLICY_NOT_ACKNOWLEDGED` on every
 * route except health, `/api/me`, and the acknowledgment endpoint. **On day one
 * every user, including the Admin, is refused everywhere until they
 * acknowledge** — which is why this screen is built first and why revision 1
 * shipping the middleware without it would have locked out the whole company.
 *
 * It is **blocking**: no dismiss path, no route underneath it, nothing else
 * mounted. And it fires on a VERSION change, not only for new users: bumping
 * `export_policy_version` in `app_settings` re-prompts everyone, and this
 * screen must handle that case identically.
 */

import { useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import {
  EXPORT_GATE_HEADING,
  EXPORT_GATE_HELP,
  EXPORT_GATE_QUESTION,
  type Me,
} from '@menlo/shared';
import { Button } from '@/components/ui/button';
import { useApiMutation } from '@/lib/queries';
import { ME_QUERY_KEY } from '@/auth/useMe';

/**
 * Whether the app must show the gate instead of anything else.
 *
 * An absent `exportPolicy` is treated as acknowledged. That is deliberate: it
 * means an older server, and the SERVER is the enforcement point (§8 item 1) —
 * a client that blocked on a missing field would be inventing a control it
 * cannot enforce while locking out users a server-side check would let
 * through.
 */
export function needsExportPolicyAcknowledgment(me: Me | undefined): boolean {
  return me?.exportPolicy !== undefined && !me.exportPolicy.acknowledged;
}

export function ExportPolicyGate({ me }: { me: Me }): React.ReactElement {
  const queryClient = useQueryClient();
  const policyVersion = me.exportPolicy?.policyVersion ?? '';

  const acknowledge = useApiMutation<{ acknowledged: boolean }, void>(
    () => ({
      path: '/api/me/acknowledge-export-policy',
      method: 'POST',
      body: { policyVersion },
    }),
    {
      onSuccess: () => {
        // Everything was 403 until this moment; the whole cache is stale.
        void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
        void queryClient.invalidateQueries();
      },
    },
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-6">
      <div className="w-full max-w-2xl rounded-lg border bg-card p-8 shadow-sm">
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" aria-hidden="true" />
          <div>
            <h1 className="text-lg font-semibold">{EXPORT_GATE_HEADING} — before you continue</h1>
            <p className="mt-1 text-xs text-muted-foreground">
              Policy version {policyVersion || 'unversioned'} · acknowledged once per version
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-4 text-sm leading-relaxed">
          <p className="font-medium">
            This system is not authorized to hold export-controlled technical data, and never holds
            any.
          </p>
          <p>
            Every opportunity you create asks one question before any other field:{' '}
            <em>{EXPORT_GATE_QUESTION}</em>
          </p>
          <p>{EXPORT_GATE_HELP}</p>
          <p>
            Answering <strong>&ldquo;Yes, or I&rsquo;m not sure&rdquo;</strong> stops the form and
            creates no record. Every upload asks the same question again, because a file is the one
            path this system cannot inspect. Both answers are recorded with your name, the time, and
            this policy version.
          </p>

          {/* Admin-configured, from `app_settings` — never hardcoded (§8). */}
          {me.exportPolicy?.refusalText !== undefined && (
            <div className="rounded-md border bg-muted/50 p-3 text-xs">
              <p className="mb-1 font-medium">What to do instead, when the answer is yes</p>
              <p className="whitespace-pre-line text-muted-foreground">
                {me.exportPolicy.refusalText}
              </p>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Free-text fields are scanned on save and will warn — never block — when they look like
            controlled content. Anything you type here is disclosable in a data-subject access
            request.
          </p>
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            There is no way past this screen. Until you acknowledge, every request is refused.
          </p>
          <Button
            onClick={() => acknowledge.mutate()}
            disabled={acknowledge.isPending || policyVersion === ''}
          >
            {acknowledge.isPending ? 'Recording…' : 'I understand'}
          </Button>
        </div>
      </div>
    </div>
  );
}
